/**
 * ShareModal — universal Share UX.
 *
 * Behaviour (per product spec):
 *   1. On devices that expose `navigator.share` (i.e. most mobile + some
 *      desktop Safari), a single "Share" button opens the native share
 *      sheet. If the user cancels or the API throws, we transparently
 *      fall back to the custom modal.
 *   2. When Web Share is unavailable, the custom modal shows a fixed
 *      channel list: Copy Link, WhatsApp, Telegram, Facebook, X, LinkedIn,
 *      Email (and SMS on mobile only).
 *
 * Consumers just render <ShareButton url title description /> — see below.
 */
import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Share2,
  X,
  Copy,
  Check,
  Mail,
  MessageCircle,
  MessageSquare,
  Linkedin,
  Facebook,
  Send,
} from "lucide-react";
import { toast } from "sonner";

// Simple mobile heuristic — mirrors what the platform actually reports.
/** True on real touch-first mobile devices (iOS + Android). Chrome on
 *  macOS ALSO exposes `navigator.share` but must NOT get the native sheet —
 *  users expect the branded modal on desktop. Detection combines UA
 *  (fast path) with `maxTouchPoints` + a viewport width guard so tablets
 *  wider than 1024px still get the desktop modal. */
function isMobileDevice() {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  const isTouch = (navigator.maxTouchPoints || 0) > 1;
  const narrow = window.innerWidth <= 1024;
  return isTouch && narrow;
}

function hasWebShare() {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/** SINGLE centralised share handler used by every card / page / component
 *  across the site. Never triggers both surfaces from one call:
 *    - On real mobile with `navigator.share` → opens the native sheet.
 *    - Anywhere else (desktop, incl. Chrome/macOS) → opens the custom modal.
 *  `event` may be null when called programmatically.
 *
 *  The native share payload is INTENTIONALLY minimal — `{title, url}` only —
 *  because iOS Safari concatenates a `text` field with the URL when the user
 *  taps "Copy", which pollutes the clipboard with the SEO description.
 *  Do NOT add `text`, `description`, `seoDescription`, or a summary here. */
export async function handleShare(event, { url, title } = {}, openCustomShareModal) {
  if (event && typeof event.preventDefault === "function") {
    event.preventDefault();
    event.stopPropagation();
  }
  if (!url) return;

  let cleanUrl = url;
  try {
    if (typeof window !== "undefined") {
      cleanUrl = new URL(url, window.location.origin).href;
    }
  } catch { /* fall back to raw url */ }

  if (isMobileDevice() && hasWebShare()) {
    try {
      // MINIMAL payload — title + url ONLY. See comment above.
      await navigator.share({ title: title || "", url: cleanUrl });
      return;
    } catch (err) {
      // User cancelled → do NOT open the modal as well.
      if (err && err.name === "AbortError") return;
      // Anything else (permission denied / share sheet unavailable) →
      // safe fallback to the branded modal.
    }
  }
  if (typeof openCustomShareModal === "function") openCustomShareModal();
}

// URL encoder that also strips undefined -> ''.
const enc = (s) => encodeURIComponent(String(s || ""));

function buildChannels({ url, title, description }, mobile) {
  const summary = description || title || "";
  const channels = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      // Public wa.me link — works on both mobile & desktop web.
      href: `https://wa.me/?text=${enc(`${title}\n${url}`)}`,
      color: "#25D366",
    },
    {
      id: "telegram",
      label: "Telegram",
      icon: Send,
      href: `https://t.me/share/url?url=${enc(url)}&text=${enc(title)}`,
      color: "#26A5E4",
    },
    {
      id: "x",
      label: "X",
      icon: null, // stylized text fallback below
      xText: "𝕏",
      href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`,
      color: "#ffffff",
    },
    {
      id: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      color: "#1877F2",
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
      color: "#0A66C2",
    },
    {
      id: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${enc(title)}&body=${enc(`${summary}\n\n${url}`)}`,
      color: "#a3a3a3",
    },
  ];
  if (mobile) {
    channels.push({
      id: "sms",
      label: "SMS",
      icon: MessageSquare,
      // `sms:` handler works on iOS & Android; body opens the native
      // messaging app pre-populated.
      href: `sms:?body=${enc(`${title} ${url}`)}`,
      color: "#22c55e",
    });
  }
  return channels;
}

export function ShareButton({
  url,
  title,
  description = "",
  className = "",
  variant = "solid", // "solid" | "ghost" | "icon"
  size = "md",
  testid = "share-btn",
  label = "Share",
}) {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    setMobile(isMobileDevice());
  }, []);

  const onClick = useCallback(
    (e) => handleShare(e, { url, title }, () => setOpen(true)),
    [url, title],
  );

  // Style presets — match the existing button system.
  const baseClasses =
    "inline-flex items-center gap-2 rounded-full font-mono tracking-[0.15em] uppercase text-[10px] transition-colors";
  const sizes = {
    sm: "px-3 py-1.5 text-[10px]",
    md: "px-4 py-2 text-[11px]",
    lg: "px-5 py-2.5 text-xs",
  };
  const variants = {
    solid: "bg-red-brand text-white hover:bg-red-500",
    ghost:
      "border border-white/15 text-white/75 hover:border-red-brand hover:text-red-brand bg-white/[0.02]",
    icon:
      "border border-white/15 text-white/75 hover:border-red-brand hover:text-red-brand bg-white/[0.02] w-9 h-9 justify-center p-0 rounded-full",
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`${baseClasses} ${sizes[size] || sizes.md} ${variants[variant] || variants.ghost} ${className}`}
        aria-label={label}
        title={label}
      >
        <Share2 size={variant === "icon" ? 16 : 14} />
        {variant !== "icon" && <span>{label}</span>}
      </button>
      <ShareModal
        open={open}
        onClose={() => setOpen(false)}
        url={url}
        title={title}
        description={description}
        mobile={mobile}
      />
    </>
  );
}

export function ShareModal({ open, onClose, url, title, description, mobile: mobileProp }) {
  const [copied, setCopied] = useState(false);
  const [mobile, setMobile] = useState(!!mobileProp);
  useEffect(() => {
    if (typeof mobileProp === "boolean") return;
    setMobile(isMobileDevice());
  }, [mobileProp]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for legacy browsers that block async clipboard on iframe.
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success("Link copied to clipboard");
      } catch {
        toast.error("Could not copy link — long-press to copy manually.");
      }
      el.remove();
    }
  }, [url]);

  const channels = buildChannels({ url, title, description }, mobile);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          data-testid="share-modal-overlay"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.92, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 20, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 shadow-[0_0_60px_rgba(239,43,43,0.15)]"
            data-testid="share-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">
                  // SHARE
                </div>
                <div
                  className="font-display text-xl text-white mt-1 leading-tight line-clamp-2"
                  data-testid="share-modal-title"
                >
                  {title}
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="share-modal-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/30 flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            {/* Link preview + copy */}
            <div className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 mb-5">
              <div
                className="font-mono text-xs text-white/70 truncate flex-1"
                data-testid="share-modal-url"
              >
                {url}
              </div>
              <button
                onClick={copyLink}
                data-testid="share-copy-link"
                className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-[0.15em] uppercase px-2.5 py-1.5 rounded-full border border-white/10 text-white/80 hover:border-red-brand hover:text-red-brand transition-colors"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            {/* Channel grid */}
            <div className="grid grid-cols-4 gap-3" data-testid="share-modal-channels">
              {channels.map((ch) => {
                const Icon = ch.icon;
                return (
                  <a
                    key={ch.id}
                    href={ch.href}
                    target={ch.id === "email" || ch.id === "sms" ? "_self" : "_blank"}
                    rel="noopener noreferrer"
                    data-testid={`share-channel-${ch.id}`}
                    className="flex flex-col items-center gap-1.5 p-2 rounded-lg border border-white/5 hover:border-red-brand/70 hover:bg-white/[0.03] transition-colors"
                    aria-label={`Share on ${ch.label}`}
                  >
                    <span
                      className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5"
                      style={{ color: ch.color }}
                    >
                      {Icon ? <Icon size={18} /> : <span className="font-bold text-lg">{ch.xText || "?"}</span>}
                    </span>
                    <span className="text-[10px] font-mono tracking-[0.1em] uppercase text-white/60">
                      {ch.label}
                    </span>
                  </a>
                );
              })}
            </div>

            <div className="mt-5 text-[10px] font-mono tracking-[0.15em] uppercase text-white/30 text-center">
              // POWERED BY DJ SPY
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ShareButton;
